<template>
  <div class="device-management-container">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <span>{{ $t("message.device.title") }}</span>
          <el-button v-if="canAddDeviceList" type="primary" @click="handleAddDevice">
            {{ $t("message.device.add") }}
          </el-button>
        </div>
        <!-- Search Form -->
        <el-form :inline="true" :model="queryParams" class="search-form">
          <el-form-item :label="$t('message.device.company')">
            <el-tree-select
              v-model="queryParams.companyId"
              :data="companyTreeData"
              :props="treeProps"
              :placeholder="$t('message.device.selectCompany')"
              check-strictly
              clearable
              filterable
              style="width: 200px"
              @change="handleSearch"
            />
          </el-form-item>

          <el-form-item :label="$t('message.device.name')">
            <el-input
              v-model="queryParams.name"
              :placeholder="$t('message.device.inputName')"
              clearable
              @change="handleSearch"
              @keyup.enter="handleSearch"
            />
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="handleSearch">
              {{ $t("message.device.search") }}
            </el-button>
            <el-button @click="resetSearch">
              {{ $t("message.device.reset") }}
            </el-button>
          </el-form-item>
        </el-form>
      </template>

      <el-table :data="deviceList" border style="width: 100%">
        <el-table-column
          v-if="isColumnVisible"
          prop="id"
          :label="$t('message.device.id')"
          width="80"
          sortable
        />
        <el-table-column
          prop="name"
          :label="$t('message.device.name')"
          width="150"
          sortable
        />
        <el-table-column
          prop="device_code"
          :label="$t('message.device.code')"
          width="150"
          sortable
        />
        <el-table-column
          prop="company_name"
          :label="$t('message.device.company')"
          width="150"
          sortable
        />
        <el-table-column
          prop="gateway_name"
          :label="$t('message.device.gateway')"
          width="150"
          sortable
        />
        <el-table-column
          prop="template_name"
          :label="$t('message.device.template')"
          width="150"
          sortable
        />
        <el-table-column
          prop="template_id"
          :label="$t('message.device.template_id')"
          width="150"
          v-if="isColumnVisible"
          sortable
        />
        <el-table-column
          v-if="isColumnVisible"
          :label="$t('message.device.state')"
          width="100"
          sortable
        >
          <template #default="scope">
            <el-tag :type="scope.row.state === 1 ? 'success' : 'danger'">
              {{
                scope.row.state === 1
                  ? $t("message.device.online")
                  : $t("message.device.offline")
              }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          prop="location"
          :label="$t('message.device.location')"
          width="150"
          sortable
        />
        <el-table-column
          prop="created_at"
          :label="$t('message.device.createTime')"
          width="180"
          sortable
        >
          <template #default="scope">
            {{ formatDate(scope.row.created_at) }}
          </template>
        </el-table-column>

        <el-table-column :label="$t('message.device.actions')" width="240">
          <template #default="scope">
             <el-button v-if="canViewDeviceList" size="small" @click="handleViewDevice(scope.row)">
              {{ $t("message.device.view") }}
            </el-button>

            <el-button  v-if="canEditDeviceList" size="small" @click="handleEditDevice(scope.row)">
              {{ $t("message.device.edit") }}
            </el-button>
            <el-button
              v-if="canDeleteDeviceList"
              size="small"
              type="danger"
              @click="handleDeleteDevice(scope.row)"
            >
              {{ $t("message.device.delete") }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.currentPage"
          v-model:page-size="pagination.pageSize"
          :total="pagination.total"
          :page-sizes="[10, 20, 30, 50]"
          layout="total, sizes, prev, pager, next, jumper"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </el-card>

    <!-- Device Edit Dialog -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="50%"
      :close-on-click-modal="false"
    >
      <device-form
        v-if="dialogVisible"
        ref="deviceFormRef"
        :device-data="currentDevice"
        @submit="handleSubmitDevice"
      />

      <template #footer>
        <el-button @click="dialogVisible = false">
          {{ $t("message.device.cancel") }}
        </el-button>
        <el-button type="primary" @click="submitDeviceForm">
          {{ $t("message.device.save") }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { useI18n } from "vue-i18n";
import DeviceForm from "./DeviceForm.vue";
import {
  getDevices,
  deleteDevice,
  createDevice,
  updateDevice,
} from "@/api/DeviceManagement";
import { getCompanyTree } from "@/api/CompanyManager";
import { usePermission } from '@/composables/usePermission'

const {  canViewDeviceList,canAddDeviceList,canEditDeviceList,canDeleteDeviceList } = usePermission()

import { useRouter } from "vue-router";


const router = useRouter();


const { t } = useI18n();

// 响应式数据
const deviceList = ref([]);
const currentDevice = ref({});
const dialogVisible = ref(false);
const dialogTitle = ref("");
const deviceFormRef = ref();
const companyTreeData = ref([]);
const isColumnVisible = ref(false);

// 查询参数
const queryParams = reactive({
  companyId: null,
  name: "",
  pageNum: 1,
  pageSize: 10,
});

// 分页
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0,
});

const queryCompanyTreeParams = reactive({
  companyId: null,
});

const treeProps = {
  value: "id",
  label: "name",
  children: "children",
};

// 方法定义
const loadCompanyTree = async () => {
  try {
    const res = await getCompanyTree(queryCompanyTreeParams);
    companyTreeData.value = res.data.list;

    if (companyTreeData.value.length > 0 && !queryParams.companyId) {
      queryParams.companyId = companyTreeData.value[0].id;
    }
  } catch (error) {
    ElMessage.error(t("message.device.loadCompanyFailed"));
  }
};

const fetchDevices = async () => {
  try {
    const params = {
      companyId: queryParams.companyId,
      name: queryParams.name,
      pageNum: queryParams.pageNum,
      pageSize: queryParams.pageSize,
    };

    const res = await getDevices(params);
    if(!res.success)
    {
      ElMessage.error(res?.msg);  
      return;
    }
    deviceList.value = res.data.list;
    pagination.total = res.data.totalCount;
  } catch (error) {
    ElMessage.error(error.message || t("message.device.fetchError"));
  }
};

const handleSearch = () => {
  queryParams.pageNum = 1;
  // 验证 companyId
  if (!queryParams.companyId || queryParams.companyId <= 0) {
    ElMessage.warning(t("message.template.selectCompanyFirst"));
    return;
  }
  fetchDevices();
};

const resetSearch = () => {
  queryParams.name = "";
  queryParams.pageNum = 1;
  handleSearch();
};

const handleAddDevice = () => {
  currentDevice.value = {
    company_id: null,
    name: "",
    device_code: "",
    description: "",
    gateway_id: null,
    template_id: null,
    state: 1,
    location: "",
  };
  dialogTitle.value = t("message.device.addTitle");
  dialogVisible.value = true;
};

const handleViewDevice = (device) => {

  router.push(`realTimeData?deviceId=${device.id}&deviceName=${encodeURIComponent(device.name)}`);
  /*   router.push({
    pathname: '/realTimeData',
    query: { deviceId: device.id }, // 可选，用于分享链接时保持数据
  }, undefined, {
    state: { device } // 主要数据通过 state 传递
  }); */
  
};



const handleEditDevice = (device) => {
  currentDevice.value = { ...device };
  if (
    currentDevice.value.gateway_id === null ||
    currentDevice.value.gateway_id === 0
  ) {
    currentDevice.value.gateway_id = null;
  }

  //ElMessage.error(`${currentDevice.value.template_id} ${currentDevice.value.template_name}  handleEditDevice`);
  dialogTitle.value = t("message.device.editTitle");
  dialogVisible.value = true;
};

const handleDeleteDevice = (device) => {
  ElMessageBox.confirm(
    t("message.device.deleteConfirm", { name: device.name }),
    t("message.common.tip"),
    {
      confirmButtonText: t("message.common.confirm"),
      cancelButtonText: t("message.common.cancel"),
      type: "warning",
    }
  )
    .then(async () => {
      try {
        const params = {
          id: device.id,
        };
        const retData = await deleteDevice(params);
        if (retData.success) {
          ElMessage.success(t("message.device.deleteSuccess"));
          fetchDevices();
        } else {
          ElMessage.error(retData?.msg ||  t("message.device.deleteFailed"));
        }
      } catch (error) {
        ElMessage.error(error.message || t("message.device.deleteFailed"));
      }
    })
    .catch(() => {
      ElMessage.info(t("message.common.deleteCancelled"));
    });
};

const submitDeviceForm = async () => {
  const ret = await deviceFormRef.value.submitForm();
  if (ret.success) {
    dialogVisible.value = false;
    fetchDevices();
  }
};

const handleSubmitDevice = async (formData) => {
  try {
    // 这里调用添加或更新设备的API
    let ret;
    if (formData.id) {
      // 如果 formData.id 存在，调用 updateDevice
      ret = await updateDevice(formData);
    } else {
      // 如果 formData.id 不存在，调用 createDevice
      ret = await createDevice(formData);
    }

    // 检查操作是否成功
    if (ret.success) {
      // 根据 formData.id 是否存在，显示相应的成功消息
      ElMessage.success(
        t(
          formData.id
            ? "message.device.updateSuccess"
            : "message.device.addSuccess"
        )
      );
    } else {
      // 如果操作失败，显示错误消息
      ElMessage.error(ret?.msg  || t("message.common.saveFailed") );
    }

    dialogVisible.value = false;
    fetchDevices();
  } catch (error) {
    ElMessage.error(error.message || t("message.device.submitError"));
  }
};

const handlePageChange = (val) => {
  queryParams.pageNum = val;
  fetchDevices();
};

const handleSizeChange = (val) => {
  queryParams.pageSize = val;
  fetchDevices();
};

const formatDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(/\//g, "-") // 将斜杠替换为横杠
    .replace(/(\d+):(\d+):(\d+)/, "$1:$2:$3"); // 保持时间格式
};
// 初始化
onMounted(async () => {
  await loadCompanyTree();
  await  handleSearch();  
});
</script>

<style scoped>
.device-management-container {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.search-form {
  margin-top: 15px;

  :deep(.el-form-item) {
    margin-right: 15px;
    margin-bottom: 0;
  }
}

@media (max-width: 768px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .search-form {
    :deep(.el-form-item) {
      margin-right: 0;
      margin-bottom: 10px;
      width: 100%;

      .el-input,
      .el-tree-select {
        width: 100%;
      }
    }
  }
}
.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>
